import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Users, Coins, BellRing, CheckCircle2, ArrowRight, Briefcase, TrendingUp, MapPin, Sparkles } from 'lucide-react';
import { logout } from '@/utils/api';

const TUTOR_TESTIMONIALS = [
  { name: 'Sandeep, Physics Tutor', text: 'Lead quality is better when profile is complete.' },
  { name: 'Priya, English Tutor', text: 'The lead status tracker helps me follow up properly.' },
  { name: 'Kamal, Maths Tutor', text: 'I get local student requirements frequently.' }
];

const TUTOR_CITY_LINKS = ['Chandigarh', 'Mohali', 'Panchkula', 'Zirakpur', 'Kharar', 'New Chandigarh'];
const LEAD_PREVIEWS = [
  { subject: 'Mathematics', level: 'Class 10', location: 'Chandigarh - Sector 35', mode: 'Home Tuition', budget: '₹400-₹600/hr' },
  { subject: 'Physics', level: 'Class 12', location: 'Mohali - Phase 7', mode: 'Online', budget: '₹500-₹800/hr' },
  { subject: 'English Spoken', level: 'Working Professional', location: 'Panchkula - Sector 15', mode: 'Online / Weekend', budget: '₹300-₹500/hr' }
];

export default function TutorsLandingPage({ user }) {
  const navigate = useNavigate();
  const isTutor = user?.role === 'tutor';
  const leadsLink = isTutor ? '/tutor/dashboard?tab=jobs' : '/auth?role=tutor&mode=signup';
  const handleLeadsIntent = async (e) => {
    e?.preventDefault?.();
    if (isTutor) {
      navigate('/tutor/dashboard?tab=jobs');
      return;
    }
    if (user && user.role !== 'tutor') {
      await logout();
      return;
    }
    navigate('/auth?role=tutor&mode=signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-sky-50/40">
      <header className="bg-white/90 backdrop-blur border-b sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-outfit font-bold text-gray-900">Tricity Tutors</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={handleLeadsIntent}>
              Get Student Leads
            </Button>
          </div>
        </nav>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-indigo-200/45 blur-3xl" />
          <div className="absolute bottom-0 -left-10 w-72 h-72 rounded-full bg-cyan-200/35 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="mb-4 bg-indigo-100 text-indigo-700">Tutors</Badge>
            <h1 className="text-4xl md:text-5xl font-outfit font-bold text-gray-900 leading-tight tracking-tight">
              Get student leads in your subject.
            </h1>
            <p className="mt-4 text-gray-600 text-lg max-w-xl">
              Build your profile, get matching requirements, and unlock only the leads you want.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-full bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 h-12 px-8" onClick={handleLeadsIntent}>
                Get Student Leads
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-600">
              <div className="flex items-center gap-2 bg-white/80 border rounded-full px-3 py-2"><Briefcase className="w-4 h-4 text-indigo-600" />Active leads</div>
              <div className="flex items-center gap-2 bg-white/80 border rounded-full px-3 py-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Track pipeline</div>
              <div className="flex items-center gap-2 bg-white/80 border rounded-full px-3 py-2"><MapPin className="w-4 h-4 text-orange-500" />City-focused demand</div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm text-gray-700">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <span>Complete profile = better matches and higher response.</span>
            </div>
          </div>
          <Card className="border-indigo-200 shadow-xl bg-white">
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-700">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-indigo-600 mt-0.5" />
                <p><span className="font-semibold">Create profile:</span> Add subjects, classes and location.</p>
              </div>
              <div className="flex items-start gap-3">
                <BellRing className="w-5 h-5 text-orange-500 mt-0.5" />
                <p><span className="font-semibold">Get matched queries:</span> Receive relevant requirements.</p>
              </div>
              <div className="flex items-start gap-3">
                <Coins className="w-5 h-5 text-emerald-600 mt-0.5" />
                <p><span className="font-semibold">Unlock and convert:</span> Pay per lead, manage status.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="flex items-center justify-between mb-4 gap-4">
          <h2 className="text-xl font-outfit font-bold text-gray-900">Recent Lead Preview</h2>
          <Badge className="bg-indigo-100 text-indigo-700">Contact details hidden until unlock</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LEAD_PREVIEWS.map((lead) => (
            <Card key={`${lead.subject}-${lead.level}-${lead.location}`} className="border-indigo-100">
              <CardContent className="pt-6">
                <p className="text-base font-semibold text-gray-900">{lead.subject}</p>
                <p className="text-sm text-gray-600 mt-1">{lead.level}</p>
                <p className="text-sm text-gray-600">{lead.location}</p>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <Badge variant="outline">{lead.mode}</Badge>
                  <span className="font-medium text-gray-700">{lead.budget}</span>
                </div>
                <p className="mt-3 text-xs text-gray-500">Phone and full details visible after unlock in dashboard.</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <h3 className="font-semibold">Subject-Relevant Leads</h3>
              <p className="text-sm text-gray-600 mt-1">See queries aligned to your subjects.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <h3 className="font-semibold">Lead Status Tracking</h3>
              <p className="text-sm text-gray-600 mt-1">Track New to Converted in dashboard.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <h3 className="font-semibold">Profile + Reviews</h3>
              <p className="text-sm text-gray-600 mt-1">Improve trust and response quality.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="text-xl font-outfit font-bold text-gray-900 mb-4">Tutor feedback</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TUTOR_TESTIMONIALS.map((item) => (
            <Card key={item.name} className="border-gray-200">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-700">&ldquo;{item.text}&rdquo;</p>
                <p className="text-xs text-gray-500 mt-3">{item.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 md:pb-12">
        <h2 className="text-xl font-outfit font-bold text-gray-900 mb-4">Browse demand by city</h2>
        <div className="flex flex-wrap gap-2">
          {TUTOR_CITY_LINKS.map((city) => (
            <Link key={city} to={`/for-tutors?city=${encodeURIComponent(city)}`}>
              <Badge className="bg-white border text-gray-700 hover:bg-indigo-50 cursor-pointer">{city}</Badge>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-2xl font-outfit font-bold">Ready to fill your schedule?</p>
            <p className="text-gray-300 text-sm">Open your dashboard and start unlocking matching leads.</p>
          </div>
          <Button asChild size="lg" className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-12 px-8">
            <Link to={leadsLink} onClick={handleLeadsIntent}>
              Start Getting Leads
            </Link>
          </Button>
        </div>
      </section>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t p-3 z-50">
        <Button className="w-full h-11 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={handleLeadsIntent}>
          Get Student Leads
        </Button>
      </div>
    </div>
  );
}
